"use client";

import {
  FormEvent,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FormProperties,
  SwitchCallback,
} from "@/app/[locale]/auth/login/components/types";
import * as countries from "i18n-iso-countries";
import registerUser, {
  isSuccessResponse,
  RegistrationErrorResponse,
  RegistrationSuccessResponse,
} from "@/lib/auth/registerUser";
import { PipelineUserDetails } from "@/lib/auth/getThirdPartyAuthProviders";
import ValidatedField from "@/app/[locale]/auth/login/components/UI/ValidatedField";
import validateRegistrationFields, {
  RegistrationValidationParams,
} from "@/lib/auth/validateRegistrationFields";
import { sendUserAccountRegisteredClient } from "@/lib/segment/logistration";
import {
  getCorrelationID,
  pageTrackingObjectUpdate,
} from "@/utils/tagular/helpers";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
  PageNames,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import RedirectLogistration from "@/app/[locale]/auth/login/components/RedirectLogistration";
import { getEnv } from "@/env";
import AlertBox from "@/app/[locale]/auth/login/components/UI/AlertBox";
import { Button } from "@/components/ui/button";
import MarketingOptIn from "@/components/MarketingOptIn/MarketingOptIn";

// All that edX formally supports:
// import arLocale from "i18n-iso-countries/langs/ar.json";
import enLocale from "i18n-iso-countries/langs/en.json";
import useLogistrationPageRedesign from "@/monarch/rules/logistrationPageRedesign/useLogistrationPageRedesign";
import useFormEvents from "@/hooks/eventing/useFormtEvents";
import debounce from "@/utils/debounce";
import FormSwitchLink from "./FormSwitchLink";
import { useModalState } from "@/contexts/AuthModalProvider";
import { getUserFieldErrorMessage } from "../utils";
import { getUserInfoFromJWT } from "@/lib/auth/getUserInfo";
// import esLocale from "i18n-iso-countries/langs/es.json";
// import frLocale from "i18n-iso-countries/langs/fr.json";
// import zhLocale from "i18n-iso-countries/langs/zh.json";
// import caLocale from "i18n-iso-countries/langs/ca.json";
// import heLocale from "i18n-iso-countries/langs/he.json";
// import idLocale from "i18n-iso-countries/langs/id.json";
// import koLocale from "i18n-iso-countries/langs/ko.json";
// import plLocale from "i18n-iso-countries/langs/pl.json";
// import ptLocale from "i18n-iso-countries/langs/pt.json";
// import ruLocale from "i18n-iso-countries/langs/ru.json";
// import ukLocale from "i18n-iso-countries/langs/uk.json";

type ValidationFields = keyof RegistrationValidationParams;

countries.registerLocale(enLocale);

interface RegistrationProps {
  profileSuggestedDetails: PipelineUserDetails;
  switchToLogin: SwitchCallback;
  isModal?: boolean;
  onClose?: () => void;
  updateLoggedInStatus?: () => void;
}

export default function RegistrationForm({
  profileSuggestedDetails,
  currentProvider,
  finishAuthUrl,
  switchToLogin,
  isModal = false,
  onClose,
  updateLoggedInStatus,
}: FormProperties & RegistrationProps) {
  const hasMarketingOptIn =
    getEnv("NEXT_PUBLIC_MARKETING_EMAILS_OPT_IN") === "true";
  const baseUrl = getEnv("NEXT_PUBLIC_SITE_URL", "https://www.edx.org");
  const { executeRecaptcha, isRecaptchaAvailable } = useRecaptcha();
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const { modalCallback } = useModalState();

  const [registrationResponse, setRegistrationResponse] =
    useState<RegistrationSuccessResponse | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(true);

  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const [nameValidationError, setNameValidationError] = useState("");
  const [usernameValidationError, setUsernameValidationError] = useState("");
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([]);
  const [emailValidationError, setEmailValidationError] = useState("");
  const [passwordValidationError, setPasswordValidationError] = useState("");

  useEffect(() => {
    pageTrackingObjectUpdate(PageNames.AccountCreation, true);
  }, []);

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: "create-account-click",
      location: "registration",
      name: "registration-page",
      text: "Create an account for free",
    },
  });

  const { elementClicked: switchToLoginClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Link,
      location: "registration-page",
      name: "sign-in-redirect",
      text: "Sign In",
    },
  });

  const { fieldInputted } = useFormEvents({
    formContext: {
      formBrand: `${baseUrl}/auth/register`,
      formName: "Registration",
      formType: "Registration",
    },
  });

  const { elementViewed: errorMessageViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      htmlId: "message-banner",
      location: "registration",
      name: "registration-banner",
      text: "Error Message",
    },
  });

  const debouncedFieldInput = useMemo(
    () =>
      debounce((fieldName: string) => {
        fieldInputted({
          correlationId: getCorrelationID(),
          userInputField: {
            fieldId: fieldName,
            fieldLabel: fieldName,
            fieldName: fieldName,
            fieldType: fieldName,
            fieldValue: "SANITIZED",
          },
        });
      }, 500),
    [fieldInputted],
  );

  const { ref: registerRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);
  const { data } = useLogistrationPageRedesign();
  const showPublicUsername = data?.showPublicUsername ?? true;
  const registerInfo = data?.registerInfo;
  const registerTitle = registerInfo?.formTitle || "";
  const registerCta = registerInfo?.formCta || "";

  const validateFields = async (
    params: RegistrationValidationParams,
    reset: ValidationFields,
  ) => {
    const result = await validateRegistrationFields(params);
    switch (reset) {
      case "name":
        setNameValidationError("");
        break;
      case "email":
        setEmailValidationError("");
        break;
      case "username":
        setUsernameValidationError("");
        break;
      case "password":
        setPasswordValidationError("");
        break;
    }

    if (result.rate_limited) {
      return;
    }

    let validationErrors = 0;

    if (result.validation_decisions) {
      if (typeof result.validation_decisions.name === "string") {
        if (result.validation_decisions.name.length >= 1) validationErrors++;
        setNameValidationError(result.validation_decisions.name);
      }
      if (typeof result.validation_decisions.email === "string") {
        if (result.validation_decisions.email.length >= 1) validationErrors++;
        setEmailValidationError(result.validation_decisions.email);
      }
      if (
        typeof result.validation_decisions.username === "string" &&
        showPublicUsername
      ) {
        if (result.validation_decisions.username.length >= 1)
          validationErrors++;
        setUsernameValidationError(result.validation_decisions.username);
      }
      if (typeof result.validation_decisions.password === "string") {
        if (result.validation_decisions.password.length >= 1)
          validationErrors++;
        setPasswordValidationError(result.validation_decisions.password);
      }
      if (result.username_suggestions) {
        setUsernameSuggestions(result.username_suggestions);
      }
    }

    setHasValidationErrors(validationErrors > 0);
  };

  const onBlur = (name: ValidationFields, value: string) => {
    void validateFields({ [name]: value }, name);
  };

  useEffect(() => {
    setName(profileSuggestedDetails.name ?? "");
    setUsername(profileSuggestedDetails.username ?? "");
    setEmail(profileSuggestedDetails.email ?? "");
  }, [profileSuggestedDetails]);

  const [countryList] = useState<React.JSX.Element[]>(
    Object.entries({
      Common: [{ code: "US", name: "United States" }],
      All: Object.entries(countries.getNames("en", { select: "official" }))
        .map(([code, name]) => ({ name, code }))
        .filter((code) => code.code !== "RU"),
    }).map(([groupName, countrySet], idx) => (
      <Fragment key={`country-set-${idx}`}>
        <optgroup label={groupName}>
          {countrySet.map((country) => (
            <option
              value={country.code}
              key={`country-set-${idx}-country-code-${country.code}`}
            >
              {country.name}
            </option>
          ))}
        </optgroup>
      </Fragment>
    )),
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");

    const formData = new FormData(event.currentTarget);
    const country = formData.get("country") as string;
    const marketing_value = hasMarketingOptIn
      ? marketingOptIn
        ? "true"
        : "false"
      : undefined;

    let validationErrors = 0;

    if (!email) {
      setEmailValidationError("Enter your email.");
      validationErrors++;
    }
    if (!username && showPublicUsername) {
      setUsernameValidationError(
        "Username must be between 2 and 30 characters.",
      );
      validationErrors++;
    }
    if (!password) {
      setPasswordValidationError("Password criteria has not been met.");
      validationErrors++;
    }
    if (!name) {
      setNameValidationError("Enter your full name.");
      validationErrors++;
    }
    if (!country) {
      // This should never happen
      setError("Country is required");
      validationErrors++;
    }

    setHasValidationErrors(validationErrors > 0);
    if (validationErrors > 0) {
      return;
    }

    // Execute reCAPTCHA before registration
    let recaptchaToken: string | undefined;
    if (isRecaptchaAvailable) {
      recaptchaToken = (await executeRecaptcha("submit_post")) as string;
      if (!recaptchaToken) {
        setError("Security verification failed. Please try again.");
        return;
      }
    }

    const result = await registerUser({
      email,
      password,
      name,
      country,
      ...(showPublicUsername && { username }),
      honor_code: "true",
      terms_of_service: "true",
      marketing_emails_opt_in: marketing_value,
      captcha_token: recaptchaToken,
    });
    if (isSuccessResponse(result)) {
      const jwt = getUserInfoFromJWT();
      // Prefer ID from response, fallback to JWT cookie
      const userIdFromResponse = result?.authenticated_user?.user_id;
      const userId = userIdFromResponse ?? jwt?.user_id ?? jwt?.sub;
      if (userId && window?.analytics?.identify) {
        window.analytics.identify(String(userId));
      }
      sendUserAccountRegisteredClient();
      setRegistrationResponse(result);
      setRegistrationSuccess(true);
      if (isModal) {
        elementClicked();
        onClose?.();
        updateLoggedInStatus?.();
        modalCallback?.();
      }
    } else {
      errorMessageViewed({
        webElement: { text: JSON.stringify(result) },
      });
      setError(getUserFieldErrorMessage(result as RegistrationErrorResponse));
      setRegistrationSuccess(false);
    }
  }

  const urlClassName =
    "inline-flex items-center text-left text-info-500 underline underline-offset-2 decoration-[#006daa4d] hover:text-[#003041] hover:decoration-[#003041]";

  return (
    <div className="w-full md:max-w-[628px] flex flex-col gap-6">
      {registerTitle && !isModal && (
        <h2 className="text-3xl md:text-5xl my-0 mt-3.5 italic font-black">
          {registerTitle}
        </h2>
      )}
      {!isModal && (
        <RedirectLogistration
          redirectUrl={registrationResponse?.redirect_url || ""}
          finishAuthUrl={finishAuthUrl}
          currentProvider={currentProvider}
          success={registrationSuccess}
          cohesionCallback={elementClicked}
        />
      )}
      {error && <AlertBox type="error" message={error} />}
      <form
        onSubmit={handleSubmit}
        ref={formRef}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-2">
          <ValidatedField
            label="Full name"
            name="name"
            autoComplete="name"
            onChange={(value) => {
              setName(value);
            }}
            onBlur={(e) => {
              onBlur("name", e.target.value);
              debouncedFieldInput("fullName");
            }}
            validationError={nameValidationError}
            value={name}
            description="This name will be used by any certificates that you earn"
            isLogistrationV1={true}
          />
          <ValidatedField
            label="Email"
            name="email"
            autoComplete="email"
            onChange={(value) => {
              setEmail(value);
            }}
            onBlur={(e) => {
              onBlur("email", e.target.value);
              debouncedFieldInput("email");
            }}
            validationError={emailValidationError}
            value={email}
            description="For account activation and important updates"
            isLogistrationV1={true}
          />
          {showPublicUsername && (
            <ValidatedField
              label="Public username"
              name="username"
              autoComplete="username"
              onChange={(value) => {
                setUsername(value);
              }}
              onBlur={(e) => {
                onBlur("username", e.target.value);
                debouncedFieldInput("username");
              }}
              validationError={usernameValidationError}
              value={username}
              suggestions={usernameSuggestions}
              description="This can not be changed later"
              isLogistrationV1={true}
            />
          )}
          <ValidatedField
            label="Password"
            name="password"
            autoComplete="new-password"
            onChange={(value) => {
              setPassword(value);
            }}
            onBlur={(e) => {
              onBlur("password", e.target.value);
              debouncedFieldInput("password");
            }}
            validationError={passwordValidationError}
            value={password}
            isLogistrationV1={true}
            isModal={isModal}
          />

          <div>
            <label
              className="text-md font-bold text-gray-800"
              htmlFor="country"
            >
              Country/Region
            </label>

            <div className="relative">
              <select
                id="country"
                className="w-full px-3 py-[9px] border border-gray-500 text-sm text-gray-700 box-border appearance-none bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 rounded-[6px]"
                name="country"
                autoComplete="country"
                onChange={() => {
                  debouncedFieldInput("country");
                }}
              >
                {countryList}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex flex-col gap-4">
            {/* Marketing consent checkbox */}
            {hasMarketingOptIn && (
              <MarketingOptIn
                optIn={marketingOptIn}
                toggleMarketingOptIn={() => setMarketingOptIn(!marketingOptIn)}
              />
            )}

            <div id="honor-code" className="text-gray-800">
              By signing up, you agree to our&nbsp;
              <a
                className={urlClassName}
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.edx.org/edx-terms-service"
              >
                Terms of Use
              </a>{" "}
              and&nbsp;
              <a
                className={urlClassName}
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.edx.org/edx-privacy-policy"
              >
                Privacy Policy
              </a>
              .
            </div>
          </div>

          <Button
            variant="brand"
            ref={registerRef}
            type="submit"
            disabled={hasValidationErrors}
            className={`text-md mt-4 ${isModal ? "w-full" : ""}`}
          >
            {registerCta || "Create an account for free"}
          </Button>
        </div>
      </form>
      {!isModal && (
        <FormSwitchLink
          prompt="Already have an account?"
          linkText="Sign in"
          onSwitch={switchToLogin}
          isModal={isModal}
          onAnalyticsClick={switchToLoginClicked}
        />
      )}
    </div>
  );
}
